import { useEffect, useState } from 'react';
import { api, type InboxItem, type InboxItemAction, type InboxItemKind } from '../api.ts';
import type { Route } from '../routes.ts';
import { Icon } from './Icon.tsx';

/**
 * What is waiting on a person, as rows that can be decided where they are.
 *
 * Shared by Home's "Needs you" panel and the Inbox's All section, and that is
 * the point of it being one component: the row a person approves on Home is
 * the row they would have approved in the Inbox, with the same words, calling
 * the same endpoint. Two lists written separately would drift in the one place
 * it matters — which verb a kind of item takes — and a "Approve" on a staged
 * write would be a button that 404s.
 *
 * Each row is decided through the queue's own endpoint rather than a new one:
 * the union endpoint is a read, and the decision keeps every check the queue's
 * route makes — `edit` on the workflow for a plan, the approver's own
 * credential for a write, admin for a claim.
 */
export function InboxList({
  items,
  limit,
  compact = false,
  isAdmin,
  highlight = null,
  onDecided,
  go,
}: {
  items: InboxItem[];
  /** Show only the first this many — Home's five. */
  limit?: number;
  /** Home: the primary verb and a way in. The Inbox shows both verbs. */
  compact?: boolean;
  isAdmin: boolean;
  /** The row a link pointed at — marked, and scrolled to once the list is in. */
  highlight?: string | null;
  /** The shell re-reads the inbox, so the badge follows without a navigation. */
  onDecided(): void;
  go(route: Partial<Route> & { tab: Route['tab'] }): void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [revising, setRevising] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  /*
   * Rows decided here, hidden at once. The shell's refresh removes them for
   * real a moment later; without this the approved row sits on screen until
   * that lands, and a second click on it is a 409 for the person who did not
   * know the first had worked.
   */
  const [gone, setGone] = useState<ReadonlySet<string>>(new Set());

  useScrollToItem(highlight, items);

  const shown = items.filter((item) => !gone.has(item.id));
  const visible = limit ? shown.slice(0, limit) : shown;

  const act = async (item: InboxItem, action: InboxItemAction) => {
    setBusy(item.id);
    setError(null);
    setNote(null);
    try {
      setNote(await decide(item, action, feedback.trim()));
      setGone((previous) => new Set([...previous, item.id]));
      setRevising(null);
      setFeedback('');
      onDecided();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const open = (item: InboxItem) =>
    go({ tab: 'inbox', section: SECTION_OF[item.kind], item: item.id });

  return (
    <div className="inbox-list-wrap">
      {error && <div className="banner danger">{error}</div>}
      {note && !error && <div className="banner">{note}</div>}
      <ul className="inbox-list">
        {visible.map((item) => {
          const [primary, secondary] = item.actions;
          // Deciding a claim is admin-only, and the server says so with a 403.
          // A Reject that always fails reads as reef refusing rather than as
          // somebody else's decision, so the row offers only the way in.
          const decidable = item.kind !== 'knowledge' || isAdmin;
          const marked = highlight === item.id;
          return (
            <li
              key={item.id}
              id={`inbox-${item.id}`}
              className={`inbox-row${marked ? ' highlighted' : ''}`}
            >
              {/*
                A category, not a severity — none of these is worse than
                another — so the pill stays neutral and the eye sorts by the
                word. Warn on all three would make the whole panel an alert.
              */}
              <span className="pill" title={SECTION_LABEL[item.kind]}>
                {KIND_LABEL[item.kind]}
              </span>
              <div className="inbox-row-body">
                <p className="inbox-row-title">{item.title}</p>
                <p className="hint">
                  {item.workflowName && <>{item.workflowName} · </>}
                  {!compact && item.detail && <>{item.detail} · </>}
                  <time dateTime={item.createdAt} title={new Date(item.createdAt).toLocaleString()}>
                    {relativeTime(item.createdAt)}
                  </time>
                </p>
                {revising === item.id && (
                  <div className="stack inbox-revise">
                    <div className="field">
                      <label htmlFor={`revise-${item.id}`}>What should it change?</label>
                      <textarea
                        id={`revise-${item.id}`}
                        value={feedback}
                        onChange={(event) => setFeedback(event.target.value)}
                        rows={3}
                        placeholder="Use the staging database rather than production."
                      />
                    </div>
                    <div className="row">
                      <button
                        onClick={() => void act(item, 'revise')}
                        disabled={busy === item.id || feedback.trim().length < 3}
                      >
                        Send it back
                      </button>
                      <button
                        className="ghost"
                        onClick={() => {
                          setRevising(null);
                          setFeedback('');
                        }}
                        disabled={busy === item.id}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="inbox-row-actions">
                {decidable && primary && revising !== item.id && (
                  <button
                    className={primary === 'approve' ? 'approve' : 'primary'}
                    onClick={() => void act(item, primary)}
                    disabled={busy === item.id}
                  >
                    {busy === item.id ? `${VERB[primary]}…` : VERB[primary]}
                  </button>
                )}
                {decidable && !compact && secondary && revising !== item.id && (
                  <button
                    onClick={() => {
                      if (secondary === 'revise') {
                        setRevising(item.id);
                        setFeedback('');
                        setNote(null);
                      } else {
                        void act(item, secondary);
                      }
                    }}
                    disabled={busy === item.id}
                  >
                    {VERB[secondary]}
                  </button>
                )}
                <button className="ghost" onClick={() => open(item)} title="Open in its queue">
                  Open <Icon name="arrow" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Which section of the Inbox holds each kind — where "Open" lands. */
export const SECTION_OF: Record<InboxItemKind, string> = {
  review: 'review',
  action: 'approvals',
  knowledge: 'knowledge',
};

const SECTION_LABEL: Record<InboxItemKind, string> = {
  review: 'Review Queue',
  action: 'Approvals',
  knowledge: 'Knowledge',
};

/**
 * What the thing *is*, not which queue it sits in: a plan, a write, a claim.
 * The queue name is on the tooltip and on the tab "Open" leads to.
 */
const KIND_LABEL: Record<InboxItemKind, string> = {
  review: 'Plan',
  action: 'Write',
  knowledge: 'Claim',
};

const VERB: Record<InboxItemAction, string> = {
  approve: 'Approve',
  revise: 'Request changes',
  apply: 'Apply',
  reject: 'Reject',
  accept: 'Accept',
};

/**
 * One item, one verb, the queue's own endpoint. The sentence returned is what
 * the person is told happened — including the case where the reviewer agreed
 * and the call itself then failed, which is a different fact from a rejection.
 */
async function decide(item: InboxItem, action: InboxItemAction, feedback: string): Promise<string> {
  switch (action) {
    case 'approve':
      await api.approveReview(item.id);
      return 'Approved — the step is carrying on.';
    case 'revise':
      await api.reviseReview(item.id, feedback);
      return 'Sent back with your note. The step revises and asks again.';
    case 'apply': {
      const result = await api.applyAction(item.id);
      return result.applied
        ? 'Applied. The call ran under your own access.'
        : `Not applied: ${result.reason ?? 'the call failed'}`;
    }
    case 'accept': {
      const result = await api.acceptKnowledge(item.id);
      return `Committed to ${result.path} as ${result.commit.slice(0, 12)}. Every run using that skill is told this from now on.`;
    }
    case 'reject':
      if (item.kind === 'knowledge') {
        await api.rejectKnowledge(item.id);
        return 'Rejected. Nothing was written, and no run behaves differently.';
      }
      await api.rejectAction(item.id);
      return 'Rejected. The step was told nothing happened.';
  }
}

/**
 * Bring the row a link named into view, once there is a row to bring.
 *
 * Keyed on the list as well as the id because the list arrives after the
 * first render: scrolling on mount finds no element, and the person who
 * followed "Open" from Home lands at the top of a queue with their row nine
 * hundred pixels down, which reads as the link having gone to the wrong place.
 * Every queue gives its rows the same `inbox-<id>` element id, so the three
 * section screens and the union list share this one hook.
 */
export function useScrollToItem(item: string | null | undefined, loaded: unknown): void {
  useEffect(() => {
    if (!item) return;
    document.getElementById(`inbox-${item}`)?.scrollIntoView({ block: 'center' });
  }, [item, loaded]);
}

/**
 * How long ago, in as few characters as it takes — the Runs screen's reading,
 * repeated here because a list of things waiting is scanned the same way.
 * Anything older than a week is a date again.
 */
export function relativeTime(iso: string): string {
  const then = new Date(iso);
  const seconds = Math.round((Date.now() - then.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604_800) return `${Math.floor(seconds / 86_400)}d ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
